/* ------------------------------------------------------------------ */
/*  Course data — shared across landing page, course page, dashboard  */
/* ------------------------------------------------------------------ */

import { z } from 'zod';
import rawData from './courseData.json';

export interface Lesson {
  id: string;
  moduleNumber: string;
  number: string;
  slug: string;
  title: string;
  description: string;
  duration: string;
  durationSeconds: number;
}

export interface Module {
  number: string;
  title: string;
  slug: string;
  isPublished?: boolean;
  lessons: Lesson[];
}

export interface CourseMeta {
  slug: string;
  title: string;
  description: string;
  thumbnailUrl: string;
}

export interface CourseContent {
  course: CourseMeta;
  modules: Module[];
  allLessons: Lesson[];
  totalLessons: number;
  totalMinutes: number;
  getLessonBySlug: (slug: string) => Lesson | undefined;
  getAdjacentLessons: (slug: string) => { prev?: Lesson; next?: Lesson };
}

/* ------------------------------------------------------------------ */
/*  JSON SCHEMA (validated at import time)                             */
/* ------------------------------------------------------------------ */

const RawLessonSchema = z.object({
  number: z.string(),
  title: z.string(),
  // Explicit id/slug override the derived `l-<prefix><number>` id and
  // slugified title — needed where slugs are authored keys not derivable
  // from the title (e.g. course 2's `cl-open-cover`).
  id: z.string().optional(),
  slug: z.string().optional(),
  description: z.string().optional(),
  duration: z.string(),
});

const RawModuleSchema = z.object({
  number: z.string(),
  title: z.string(),
  isPublished: z.boolean().optional(),
  lessons: z.array(RawLessonSchema),
});

const CourseDataSchema = z.object({
  modules: z.array(RawModuleSchema),
});

/* ------------------------------------------------------------------ */
/*  HELPERS                                                            */
/* ------------------------------------------------------------------ */

function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function parseDurationSeconds(hms: string): number {
  const [mm, ss] = hms.split(':').map(Number);
  return mm * 60 + ss;
}

export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m.toString().padStart(2, '0')}m`;
}

export function moduleDuration(lessons: Lesson[]): string {
  const totalSeconds = lessons.reduce((acc, l) => acc + l.durationSeconds, 0);
  return formatDuration(Math.round(totalSeconds / 60));
}

// R2 thumbnail variants follow the "<name>-<width>w.webp" naming
// convention; fall back to the single source when a variant is missing.
export function thumbnailSrcSet(url: string, widths: number[] = [576, 768]): string {
  return widths.map((w) => `${url.replace(/\.webp$/, `-${w}w.webp`)} ${w}w`).join(", ");
}

/* ------------------------------------------------------------------ */
/*  FACTORY                                                            */
/* ------------------------------------------------------------------ */

export function buildCourseContent(
  rawJson: unknown,
  meta: CourseMeta,
  idPrefix: string,
): CourseContent {
  const { modules: rawModules } = CourseDataSchema.parse(rawJson);

  const modules: Module[] = rawModules.map((mod) => ({
    number: mod.number,
    title: mod.title,
    slug: slugify(mod.title),
    isPublished: mod.isPublished,
    lessons: [...mod.lessons]
      .sort((a, b) => {
        const [, aLesson = "0"] = a.number.split(".");
        const [, bLesson = "0"] = b.number.split(".");
        return parseInt(aLesson, 10) - parseInt(bLesson, 10);
      })
      .map((lesson) => ({
        id: lesson.id ?? `${idPrefix}${lesson.number}`,
        moduleNumber: mod.number,
        number: lesson.number,
        slug: lesson.slug ?? slugify(lesson.title),
        title: lesson.title,
        description: lesson.description ?? 'Coming soon.',
        duration: lesson.duration,
        durationSeconds: parseDurationSeconds(lesson.duration),
      })),
  }));

  const allLessons: Lesson[] = modules.flatMap((m) => m.lessons);

  // Slug uniqueness assertion — per course (slugs only need to be unique
  // within a course per UNIQUE(course_id, slug)).
  const slugSet = new Set<string>();
  for (const lesson of allLessons) {
    if (slugSet.has(lesson.slug)) {
      throw new Error(`Duplicate lesson slug detected: ${lesson.slug}`);
    }
    slugSet.add(lesson.slug);
  }

  return {
    course: meta,
    modules,
    allLessons,
    totalLessons: allLessons.length,
    totalMinutes: modules.reduce((acc, m) => {
      return acc + m.lessons.reduce((a, l) => a + l.durationSeconds / 60, 0);
    }, 0),
    getLessonBySlug: (slug) => allLessons.find((l) => l.slug === slug),
    getAdjacentLessons: (slug) => {
      const idx = allLessons.findIndex((l) => l.slug === slug);
      if (idx === -1) return {};
      return {
        prev: allLessons[idx - 1],
        next: allLessons[idx + 1],
      };
    },
  };
}

/* ------------------------------------------------------------------ */
/*  COURSE 1 — Intro to Agentic Software Engineering                  */
/* ------------------------------------------------------------------ */

export const course = {
  slug: 'agentic-software-engineering' as const,
  title: 'Intro to Agentic Software Engineering',
  description:
    'Speed without discipline is not engineering. Learn the 5-phase agentic framework for building software with AI.',
  thumbnailUrl: 'https://assets.foyzul.com/aswe-lms/course-thumbnail.webp',
};

const content = buildCourseContent(rawData, course, 'l-');

export const courseContent: CourseContent = content;

export const modules: Module[] = content.modules;
export const allLessons: Lesson[] = content.allLessons;
export const totalLessons = content.totalLessons;
export const totalMinutes = content.totalMinutes;
export const getLessonBySlug = content.getLessonBySlug;
export const getAdjacentLessons = content.getAdjacentLessons;
